import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { SolutionTokenService } from '../services/solution-token.service';

/**
 * Espera a que el token esté emitido antes de decidir. Sin esta espera, en el
 * primer arranque las capacidades todavía no están cargadas y cualquier ruta
 * pasaba: el técnico terminaba aterrizando en el panel principal, que después
 * le respondía 403 en todas sus llamadas.
 *
 * Si el token no se puede obtener, se deja pasar y decide el backend: expulsar
 * a un usuario legítimo por un problema de red sería peor.
 */
async function capacidadesListas(solutionToken: SolutionTokenService): Promise<string[]> {
  try {
    await solutionToken.getToken();
  } catch {
    // El interceptor y el guard de sesión se encargan de los fallos de auth.
  }
  return solutionToken.capacidades();
}

/**
 * Capacidad que exige cada ruta. Vive acá para que el guard y el cambio de
 * workspace usen la misma tabla: al cambiar de workspace hay que saber si el
 * rol nuevo puede seguir en la pantalla donde está parado el usuario.
 */
export const CAPACIDAD_POR_RUTA: Record<string, string> = {
  '/dashboard': 'panel:ver',
  '/activities': 'actividades:ver',
  '/assets': 'activos:ver',
  '/schedules': 'planes:ver',
  '/checklists': 'checklists:ver',
  '/users': 'equipo:ver',
  '/work-units': 'unidades:ver',
  // Gerente y coordinador la consultan; crear y editar piden sedes:editar (P17).
  '/branches': 'sedes:ver',
  '/reports': 'reportes:ver',
};

/**
 * Bloquea una ruta cuando el rol no tiene la capacidad. Ocultar la entrada del
 * menú no alcanza: escribir la URL a mano llegaría igual a la pantalla.
 *
 * El control real vive en el backend; esto evita mostrar pantallas rotas.
 */
export function capabilityGuard(capacidad: string): CanActivateFn {
  return async () => {
    const solutionToken = inject(SolutionTokenService);
    const router = inject(Router);

    const capacidades = await capacidadesListas(solutionToken);
    if (capacidades.length === 0) return true;
    if (capacidades.includes(capacidad)) return true;

    return router.parseUrl(rutaInicial(solutionToken));
  };
}

/**
 * Primera pantalla del usuario. El técnico no tiene panel principal, así que
 * entra directo a sus actividades.
 */
export function rutaInicial(solutionToken: SolutionTokenService): string {
  // Sin capacidades no sabemos el rol, y el panel principal es justo la
  // pantalla que más roles tienen vedada: aterrizar ahí le mostraba al técnico
  // un panel que después le responde 403 en cada llamada. Las actividades las
  // puede abrir cualquiera de los cuatro roles, así que es el destino seguro
  // cuando el token no llegó.
  if (solutionToken.capacidades().length === 0) return '/activities';
  if (solutionToken.puede('panel:ver')) return '/dashboard';
  if (solutionToken.puede('actividades:ver')) return '/activities';
  return '/notifications';
}

/** Redirige la raíz a la primera pantalla que el rol sí puede abrir. */
export const landingGuard: CanActivateFn = async () => {
  const solutionToken = inject(SolutionTokenService);
  const router = inject(Router);

  await capacidadesListas(solutionToken);
  return router.parseUrl(rutaInicial(solutionToken));
};
